import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroupDirective, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Yii } from 'libraries/interfaces/Yii';
import { ELabelPosition } from 'libraries/module/formbuilder/fields/form-field/form-field.component';
import { Subscription } from 'rxjs';
import { EAirSearchFields } from "app_locobook/air-search/e-air-search-fields";

export interface IAirSearchQuantity {
    adult: number;
    child: number;
    infant: number;
}

class Wording {
    public readonly travellers: string = Yii.t('wui-shared-js', 'Voyageurs');
    public readonly adult: string = Yii.t('wui-shared-js', 'ADT');
    public readonly child: string = Yii.t('wui-shared-js', 'CHD');
    public readonly infant: string = Yii.t('wui-shared-js', 'INF');
}

@Component({
    selector: 'air-search-quantity-form',
    templateUrl: './air-search-quantity-form.component.html',
    styleUrls: ['./air-search-quantity-form.component.scss']
})
export class AirSearchQuantityFormComponent implements OnInit, OnDestroy {
    @Input()
    public disabled: boolean = false;
    public airQuantitySearchForm: FormGroup;
    public maxTravellers: { [key: string]: number };
    public wording: Wording = new Wording();
    public labelPosition: ELabelPosition = ELabelPosition.right;
    public airSearchQuantityFormSubscription: Subscription;
    private airSearchForm: FormGroup;
    private MAX_TRAVELLERS_DEFAULT: number = 9;

    public eAirSearchFields: typeof EAirSearchFields = EAirSearchFields;

    constructor(
        private formGroupDirective: FormGroupDirective,
        private fb: FormBuilder,
    ) { }

    public ngOnInit(): void {
        this.initAirSearchQuantityForm();
        this.initAirQuantitySearchFormSubscription();
        this.initMaxTravellers();
    }

    public ngOnDestroy(): void {
        this.airSearchQuantityFormSubscription.unsubscribe();
    }

    private initAirSearchQuantityForm(): void {
        this.airQuantitySearchForm = this.fb.group({
            adult: [1, [Validators.required, Validators.min(1)]],
            child: [0, [Validators.required, Validators.min(0)]],
            infant: [0, [Validators.required, Validators.min(0)]]
        });
        this.airSearchForm = this.formGroupDirective.form;
        this.airSearchForm.addControl(EAirSearchFields.quantity, this.airQuantitySearchForm);
    }

    private initMaxTravellers(): void {
        this.maxTravellers = {
            adult: this.getMaxTravellersByType(EAirSearchFields.adult),
            child: this.getMaxTravellersByType(EAirSearchFields.child),
            infant: this.getMaxTravellersByType(EAirSearchFields.infant),
        };
    }

    private getMaxTravellersByType(field: string): number {
        const adultValue: number = this.airQuantitySearchForm.get(EAirSearchFields.adult).value;
        const childValue: number = this.airQuantitySearchForm.get(EAirSearchFields.child).value;
        const infantValue: number = this.airQuantitySearchForm.get(EAirSearchFields.infant).value;
        if ((adultValue + childValue + infantValue) === this.MAX_TRAVELLERS_DEFAULT) {
            return this.airQuantitySearchForm.get(field).value
        }
        if (field === EAirSearchFields.infant) {
            return adultValue;
        }
        if (field === EAirSearchFields.child) {
            return this.MAX_TRAVELLERS_DEFAULT - adultValue;
        }
        if (field === EAirSearchFields.adult) {
            return this.MAX_TRAVELLERS_DEFAULT - childValue;
        }
        return this.MAX_TRAVELLERS_DEFAULT;
    }

    private initAirQuantitySearchFormSubscription(): void {
        this.airSearchQuantityFormSubscription = this.airQuantitySearchForm.valueChanges.subscribe((quantity: IAirSearchQuantity) => {
            if (quantity.adult > this.maxTravellers[EAirSearchFields.adult]) {
                this.airQuantitySearchForm.get(EAirSearchFields.adult).setValue(this.maxTravellers[EAirSearchFields.adult]);
            }
            if (quantity.child > this.maxTravellers[EAirSearchFields.child]) {
                this.airQuantitySearchForm.get(EAirSearchFields.child).setValue(this.maxTravellers[EAirSearchFields.child]);
            }
            if (quantity.infant > this.maxTravellers[EAirSearchFields.infant]) {
                this.airQuantitySearchForm.get(EAirSearchFields.infant).setValue(this.maxTravellers[EAirSearchFields.infant]);
            }
            if (quantity.infant > quantity.adult) {
                this.airQuantitySearchForm.get(EAirSearchFields.infant).setValue(quantity.adult);
            }
            if ((quantity.child + quantity.adult) === 0) {
                this.airQuantitySearchForm.get(EAirSearchFields.adult).setValue(1);
            }
            this.initMaxTravellers();
        });
    }

}
